import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SocialPlatform, type LinkedInOrganization } from '@mg/shared';
import { Building2, Check } from 'lucide-react';
import { api, ApiRequestError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { PLATFORM_META } from './platforms';

/**
 * "Choose your LinkedIn Company Page" picker, shown after a Community Management
 * OAuth round-trip (or to finish a pending connection). Lists the organizations
 * the member administers and connects the chosen one.
 */
export function LinkedInOrgPickerModal({
  open,
  tenantKey,
  connectionId,
  onClose,
  onConnected,
}: {
  open: boolean;
  tenantKey: string;
  connectionId: string | null;
  onClose: () => void;
  onConnected: () => void;
}) {
  const queryClient = useQueryClient();

  const orgs = useQuery({
    queryKey: ['linkedin-orgs', tenantKey, connectionId],
    queryFn: ({ signal }) => api.linkedinOrganizations(connectionId as string, signal),
    enabled: open && connectionId != null,
    staleTime: 0,
    gcTime: 0,
  });

  const select = useMutation({
    mutationFn: (organizationId: string) =>
      api.linkedinSelectOrganization(connectionId as string, organizationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['social-connections', tenantKey] });
      onConnected();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Choose your LinkedIn Company Page"
      footer={
        <Button variant="secondary" onClick={onClose} disabled={select.isPending}>
          Cancel
        </Button>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-ink-muted">
          Pick the Company Page you&apos;d like to connect. We&apos;ll publish posts as that Page.
        </p>

        {orgs.isLoading ? (
          <LoadingState label="Loading your Company Pages…" />
        ) : orgs.isError ? (
          <ErrorState
            message={
              orgs.error instanceof ApiRequestError
                ? orgs.error.message
                : 'Could not load your Company Pages.'
            }
            onRetry={() => void orgs.refetch()}
          />
        ) : (orgs.data ?? []).length === 0 ? (
          <EmptyState
            title="No Company Pages found"
            description="We didn't find a Page you can post to. You need to be a Super admin or Content admin of the Page (Analyst/Curator roles can't publish), and any pending admin invite must be accepted on LinkedIn first. If you signed in with the wrong LinkedIn account, disconnect and reconnect."
          />
        ) : (
          <ul className="space-y-2">
            {(orgs.data ?? []).map((org) => (
              <OrgRow
                key={org.id}
                org={org}
                selecting={select.isPending && select.variables === org.id}
                disabled={select.isPending}
                onSelect={() => select.mutate(org.id)}
              />
            ))}
          </ul>
        )}

        {select.isError && (
          <p className="text-sm text-red-600 dark:text-error-dark">
            {select.error instanceof ApiRequestError
              ? select.error.message
              : 'Could not connect that Company Page. Please try again.'}
          </p>
        )}
      </div>
    </Modal>
  );
}

function OrgRow({
  org,
  selecting,
  disabled,
  onSelect,
}: {
  org: LinkedInOrganization;
  selecting: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        disabled={disabled}
        className="focus-ring flex w-full items-center gap-3 rounded-xl border border-line bg-card px-3 py-2.5 text-left transition-colors hover:border-ink-ghost hover:bg-tint disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
            PLATFORM_META[SocialPlatform.LINKEDIN].tile,
          )}
        >
          <Building2 className="h-5 w-5" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-ink">{org.name}</span>
          {org.vanityName && (
            <span className="block truncate text-xs text-ink-muted">
              linkedin.com/company/{org.vanityName}
            </span>
          )}
        </span>
        {selecting ? (
          <span className="text-xs font-medium text-action">Connecting…</span>
        ) : (
          <Check className="h-5 w-5 shrink-0 text-ink-ghost" aria-hidden />
        )}
      </button>
    </li>
  );
}
